import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createNotification,
  getNotification,
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteOldNotifications,
  getUnreadNotificationCount,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from "../../firebase";

export const notificationsApi = createApi({
  reducerPath: "notificationsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Notification", "UnreadCount"],
  endpoints: (builder) => ({
    // ============================================
    // NOTIFICATIONS
    // ============================================

    // Get notifications for a clinic/therapist
    getNotifications: builder.query({
      async queryFn({
        clinicId,
        therapistId,
        unreadOnly,
        type,
        urgency,
        pageSize,
        cursor,
      }) {
        try {
          const result = await getNotifications(clinicId, {
            therapistId,
            unreadOnly,
            type,
            urgency,
            pageSize,
            cursor,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7200] Error fetching notifications:", {
            errorCode: "CK-7200",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result?.notifications
          ? [
              ...result.notifications.map(({ id }) => ({
                type: "Notification",
                id,
              })),
              { type: "Notification", id: "LIST" },
            ]
          : [{ type: "Notification", id: "LIST" }],
    }),

    // Get single notification
    getNotification: builder.query({
      async queryFn({ clinicId, notificationId }) {
        try {
          const notification = await getNotification(clinicId, notificationId);
          return { data: notification };
        } catch (error) {
          console.error("[CK-7201] Error fetching notification:", {
            errorCode: "CK-7201",
            errorMessage: error.message,
            clinicId,
            notificationId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { notificationId }) => [
        { type: "Notification", id: notificationId },
      ],
    }),

    // Get unread notification count
    getUnreadCount: builder.query({
      async queryFn({ clinicId, therapistId }) {
        try {
          const count = await getUnreadNotificationCount(clinicId, therapistId);
          return { data: count };
        } catch (error) {
          console.error("[CK-7202] Error fetching unread count:", {
            errorCode: "CK-7202",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { data: 0 };
        }
      },
      providesTags: [{ type: "UnreadCount", id: "COUNT" }],
    }),

    // Create notification
    createNotification: builder.mutation({
      async queryFn({ clinicId, notificationData, therapistId, therapistName }) {
        try {
          const notification = await createNotification(clinicId, notificationData);

          // Log activity (optional - only if therapistId provided)
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.CREATE_NOTIFICATION,
              entityType: ENTITY_TYPES.NOTIFICATION,
              entityId: notification.id,
              entityName: notification.title,
              details: {
                type: notificationData.type,
                urgency: notificationData.urgency,
                targetTherapist: notificationData.therapistId || "clinic-wide",
              },
              success: true,
            });
          }

          return { data: notification };
        } catch (error) {
          console.error("[CK-7203] Error creating notification:", {
            errorCode: "CK-7203",
            errorMessage: error.message,
            clinicId,
            notificationData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "UnreadCount", id: "COUNT" },
      ],
    }),

    // Mark notification as read
    markAsRead: builder.mutation({
      async queryFn({ clinicId, notificationId, therapistId }) {
        try {
          const notification = await markNotificationAsRead(
            clinicId,
            notificationId,
            therapistId
          );
          return { data: notification };
        } catch (error) {
          console.error("[CK-7204] Error marking notification as read:", {
            errorCode: "CK-7204",
            errorMessage: error.message,
            clinicId,
            notificationId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { notificationId }) => [
        { type: "Notification", id: notificationId },
        { type: "Notification", id: "LIST" },
        { type: "UnreadCount", id: "COUNT" },
      ],
    }),

    // Mark all notifications as read
    markAllAsRead: builder.mutation({
      async queryFn({ clinicId, therapistId }) {
        try {
          const count = await markAllNotificationsAsRead(clinicId, therapistId);
          return { data: count };
        } catch (error) {
          console.error("[CK-7205] Error marking all notifications as read:", {
            errorCode: "CK-7205",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "UnreadCount", id: "COUNT" },
      ],
    }),

    // Delete notification
    deleteNotification: builder.mutation({
      async queryFn({
        clinicId,
        notificationId,
        notificationTitle,
        therapistId,
        therapistName,
      }) {
        try {
          await deleteNotification(clinicId, notificationId);

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.DELETE_NOTIFICATION,
              entityType: ENTITY_TYPES.NOTIFICATION,
              entityId: notificationId,
              entityName: notificationTitle || "לא ידוע",
              success: true,
            });
          }

          return { data: notificationId };
        } catch (error) {
          console.error("[CK-7206] Error deleting notification:", {
            errorCode: "CK-7206",
            errorMessage: error.message,
            clinicId,
            notificationId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "UnreadCount", id: "COUNT" },
      ],
    }),

    // Delete old notifications (cleanup)
    deleteOldNotifications: builder.mutation({
      async queryFn({ clinicId, daysOld }) {
        try {
          const count = await deleteOldNotifications(clinicId, daysOld);
          return { data: count };
        } catch (error) {
          console.error("[CK-7207] Error deleting old notifications:", {
            errorCode: "CK-7207",
            errorMessage: error.message,
            clinicId,
            daysOld,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "UnreadCount", id: "COUNT" },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationQuery,
  useGetUnreadCountQuery,
  useCreateNotificationMutation,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useDeleteOldNotificationsMutation,
} = notificationsApi;

